import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["header", "headerActions", "description", "error", "isLoading", "content", "appearsIn", "navMarker", "errorMarker", "showNext", "showPrevious", "onClickNext", "onClickPrevious", "onItemClick", "use", "showDescription", "setMaxCards", "displayFullCardsOnMobile", "minCardSize", "showOverview", "size"];
import ApiError from '../../../components/Errors/ApiError';
import SectionHeading from '../../../components/SectionHeading';
import styled, { css, mediaQuery, useMediaQuery } from 'academy-common-lib/utils/Styling';
import { TFlex } from 'foundations-components/transitional/components/TFlex';
import { Button } from 'foundations-components/transitional/components/Button';
import I18n from 'I18n';
import { ConditionalNavMarker } from '../../rhumb';
import { FULL_CARD_SIZES } from '../../cards/cardsTypes';
import ContentCardSkeleton from '../../../components/TrackCard/ContentCardSkeleton';
import { useCallback, useLayoutEffect, useMemo, useRef } from 'react';
import debounce from 'hs-lodash/debounce';
import ReducedCardSkeleton from '../../../components/ContentCards/ReducedContentCard/subcomponents/ReducedCardSkeleton';
import UsageTracker from '../../../utils/UsageTracker';
import { LearningContentCard } from '../../cards/components/LearningContentCard';
import { Flex } from 'foundations-components/components/Flex';
import { Box } from 'foundations-components/transitional/components/Box';
import { LeftIcon, RightIcon } from 'foundations-assets';
import { Text } from 'foundations-components/components/Text';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const MAX_CARDS_FOR_MOBILE = 3;
const DEFAULT_MIN_CARD_SIZE = {
  'extra-large': 320,
  large: 250,
  medium: 380,
  small: 340
};
const ResponsiveRow = styled.div.withConfig({
  displayName: "CardsSection__ResponsiveRow",
  componentId: "sc-18w8khl-0"
})(["display:grid;width:100%;grid-template-columns:repeat( auto-fill,minmax( ", "px,1fr ) );grid-template-rows:1fr;grid-gap:24px;grid-auto-rows:0;row-gap:0;", ""], ({
  minCardSize,
  $size
}) => minCardSize || DEFAULT_MIN_CARD_SIZE[$size], ({
  displayFullCardsOnMobile
}) => displayFullCardsOnMobile && mediaQuery('mobile', css(["display:flex;flex-direction:column;gap:24px;"])));
export const useSetMaxCards = ({
  displayFullCardsOnMobile,
  renderArea,
  setMaxCards
}) => {
  const {
    isMobile
  } = useMediaQuery();
  const debouncedUpdate = useMemo(() => debounce(() => {
    if (!setMaxCards || !renderArea.current) return;
    if (isMobile && displayFullCardsOnMobile) {
      setMaxCards(MAX_CARDS_FOR_MOBILE);
    } else {
      const cardsThatFitOnAreaCount = window.getComputedStyle(renderArea.current).getPropertyValue('grid-template-columns').split(' ').length;
      setMaxCards(cardsThatFitOnAreaCount);
    }
  }, 200), [displayFullCardsOnMobile, isMobile, renderArea, setMaxCards]);
  useLayoutEffect(() => {
    if (!setMaxCards || !renderArea.current) return;
    debouncedUpdate();
    window.addEventListener('resize', debouncedUpdate);
    return () => window.removeEventListener('resize', debouncedUpdate);
  }, [debouncedUpdate, setMaxCards, renderArea]);
};
export const CardsSection = _ref => {
  let {
      header,
      headerActions,
      description,
      error,
      isLoading: isLoadingProp,
      content,
      appearsIn,
      navMarker,
      errorMarker,
      showNext: showNextParam,
      showPrevious: showPreviousParam,
      onClickNext,
      onClickPrevious,
      onItemClick,
      use,
      showDescription,
      setMaxCards,
      displayFullCardsOnMobile,
      minCardSize,
      showOverview,
      size = 'large'
    } = _ref,
    props = _objectWithoutPropertiesLoose(_ref, _excluded);
  const {
    isTablet
  } = useMediaQuery();
  const cardsSectionRef = useRef(null);
  useSetMaxCards({
    displayFullCardsOnMobile,
    renderArea: cardsSectionRef,
    setMaxCards
  });
  const handleOnNextClick = useCallback(() => {
    UsageTracker.track('interaction', {
      action: 'card-section-next-click',
      appearsIn
    });
    if (onClickNext) onClickNext();
  }, [appearsIn, onClickNext]);
  const handleOnPreviousClick = useCallback(() => {
    UsageTracker.track('interaction', {
      action: 'card-section-previous-click',
      appearsIn
    });
    if (onClickPrevious) onClickPrevious();
  }, [appearsIn, onClickPrevious]);
  const handleOnItemClick = useCallback((item, index) => {
    UsageTracker.track('interaction', {
      action: 'card-section-item-click',
      id: item.id,
      language: item.language,
      type: item.type,
      appearsIn,
      name: item.name,
      cardNumber: index
    });
    if (onItemClick) onItemClick(item, index);
  }, [appearsIn, onItemClick]);
  const rows = useMemo(() => content && content.map((item, i) => /*#__PURE__*/_jsx(LearningContentCard, {
    body: !showDescription ? undefined : null,
    item: item,
    onItemClick: clickedItem => handleOnItemClick(clickedItem, i),
    showOverview: showOverview,
    size: size,
    use: use
  }, `${item.id}`)), [content, handleOnItemClick, showDescription, showOverview, size, use]);
  if (error) return /*#__PURE__*/_jsx(ConditionalNavMarker, {
    name: errorMarker,
    children: /*#__PURE__*/_jsx(ApiError, {
      error: error
    })
  });
  const isLoading = isLoadingProp || !rows;
  if (!isLoading && !rows.length) return null; // if empty results don't render the component

  const showNext = !isLoading && showNextParam && (!FULL_CARD_SIZES.includes(size) || !isTablet);
  const showPrevious = !isLoading && showPreviousParam && (!FULL_CARD_SIZES.includes(size) || !isTablet);
  return /*#__PURE__*/_jsxs("div", Object.assign({}, props, {
    "data-loading": isLoading,
    style: {
      width: '100%'
    },
    children: [/*#__PURE__*/_jsxs(Flex, {
      children: [/*#__PURE__*/_jsx(Box, {
        grow: 1,
        children: header && /*#__PURE__*/_jsx(SectionHeading, {
          subText: description,
          children: header
        })
      }), /*#__PURE__*/_jsx(Text, {
        textWrap: "nowrap",
        children: headerActions
      })]
    }), /*#__PURE__*/_jsx(ConditionalNavMarker, {
      name: navMarker,
      children: /*#__PURE__*/_jsxs(TFlex, {
        align: "stretch",
        children: [showPrevious && /*#__PURE__*/_jsx(TFlex, {
          align: "center",
          direction: "column",
          inlineSize: "0 !important",
          justify: "center",
          maxBlockSize: "204px",
          children: /*#__PURE__*/_jsx(Button, {
            "aria-label": I18n.text('learningCenter.common.loadPreviousButton'),
            marginInlineEnd: "1100",
            onClick: handleOnPreviousClick,
            use: "unstyled",
            children: /*#__PURE__*/_jsx(LeftIcon, {
              color: "icon-core-subtle",
              size: "800"
            })
          })
        }), /*#__PURE__*/_jsx(ResponsiveRow, {
          $size: size,
          displayFullCardsOnMobile: displayFullCardsOnMobile ? 1 : 0,
          minCardSize: minCardSize,
          ref: cardsSectionRef,
          style: {
            overflow: isLoading ? 'hidden' : undefined
          },
          children: isLoading ? [...Array(5)].map((_, i) => FULL_CARD_SIZES.includes(size) ? /*#__PURE__*/_jsx(ContentCardSkeleton, {
            use: use
          }, i) :
          /*#__PURE__*/
          /* eslint-disable-next-line react/jsx-key */
          _jsx(ReducedCardSkeleton, {
            size: size,
            use: use
          })) : rows
        }), showNext && /*#__PURE__*/_jsx(TFlex, {
          align: "center",
          direction: "column",
          inlineSize: "0 !important",
          justify: "center",
          maxBlockSize: "204px",
          children: /*#__PURE__*/_jsx(Button, {
            "aria-label": I18n.text('learningCenter.common.loadNextButton'),
            marginInlineStart: "1100",
            onClick: handleOnNextClick,
            use: "unstyled",
            children: /*#__PURE__*/_jsx(RightIcon, {
              color: "icon-core-subtle",
              size: "800"
            })
          })
        })]
      })
    })]
  }));
};
export default CardsSection;